import {
  createTaskInputSchema,
  type BriefAsset,
  type BriefDetails,
  type CreateTaskInput,
  type MilestoneInput,
  type TaskDetail,
  type TaskPriority,
  type TaskType,
} from '@mybusinessmate/schema';
import { MILESTONE_TEMPLATES, toMilestoneInputs } from './task-milestones';

export type Platform = 'instagram' | 'facebook';

export interface PostValues {
  format: 'single_image' | 'carousel';
  platforms: Platform[];
  caption: string;
  hashtags: string[];
  publishDate: string;
  publishTime: string;
  audioNote: string;
}
export interface ReelValues {
  script: string;
  durationSeconds: 15 | 30 | 60;
  platforms: Platform[];
  caption: string;
  hashtags: string[];
  publishDate: string;
  publishTime: string;
  audioNote: string;
}
export interface WebsiteValues {
  goalAudience: string;
  domainName: string;
  domainRegistrar: string;
  style: string;
  primaryColor: string;
  secondaryColor: string;
  fonts: string;
  pages: { title: string; sections: string[] }[];
  logoNotes: string;
  generalNotes: string;
  referenceSites: string[];
  liveBy: string;
}
export interface MonthlyValues {
  pageUrl: string;
  changeKind: 'text_prices' | 'images' | 'layout' | 'new_section' | 'fix' | 'other';
  description: string;
  liveBy: string;
}
export interface CustomValues {
  details: string;
}

/** Every type's brief is kept, so switching the type tile and back does not lose what was typed; only the chosen one is sent. */
/** Someone chosen for the team of a task that does not exist yet. */
export interface TeamPick {
  id: string;
  name: string;
  staffRole: 'lead_specialist' | 'specialist';
  title: string | null;
  department: string | null;
  activeTaskCount: number;
  maxActiveTasks: number;
}

export interface TaskFormValues {
  type: TaskType;
  title: string;
  priority: TaskPriority;
  workUnits: number;
  dueDate: string;
  clientVisible: boolean;
  requiresClientApproval: boolean;
  /** The named stops on the progress track. Starts as the usual set for the type. */
  milestones: MilestoneInput[];
  /** Who the task starts with (a new task only). */
  team: TeamPick[];
  /** The person chose someone who is at their task limit and confirmed it. */
  confirmOverCapacity: boolean;
  post: PostValues;
  reel: ReelValues;
  website: WebsiteValues;
  monthly: MonthlyValues;
  custom: CustomValues;
}

/** A copy, so editing a task's stops never edits the template. */
const templateFor = (type: TaskType): MilestoneInput[] => MILESTONE_TEMPLATES[type].map((m) => ({ ...m }));

/** Moving to another kind of task swaps the stops too, unless they were already changed by hand. */
export function withType(values: TaskFormValues, type: TaskType): TaskFormValues {
  const untouched = JSON.stringify(values.milestones) === JSON.stringify(MILESTONE_TEMPLATES[values.type]);
  return { ...values, type, milestones: untouched ? templateFor(type) : values.milestones };
}

export const emptyTaskForm = (type: TaskType = 'post'): TaskFormValues => ({
  type,
  title: '',
  priority: 'Medium',
  workUnits: 1,
  dueDate: '',
  clientVisible: true,
  requiresClientApproval: true,
  milestones: templateFor(type),
  team: [],
  confirmOverCapacity: false,
  post: {
    format: 'single_image',
    platforms: ['instagram'],
    caption: '',
    hashtags: [],
    publishDate: '',
    publishTime: '',
    audioNote: '',
  },
  reel: {
    script: '',
    durationSeconds: 30,
    platforms: ['instagram'],
    caption: '',
    hashtags: [],
    publishDate: '',
    publishTime: '',
    audioNote: '',
  },
  website: {
    goalAudience: '',
    domainName: '',
    domainRegistrar: '',
    style: '',
    primaryColor: '',
    secondaryColor: '',
    fonts: '',
    pages: [],
    logoNotes: '',
    generalNotes: '',
    referenceSites: [],
    liveBy: '',
  },
  monthly: { pageUrl: '', changeKind: 'text_prices', description: '', liveBy: '' },
  custom: { details: '' },
});

/** Field errors keyed by the API's dotted path, e.g. `title`, `brief.caption`, `assets.0.key`. */
export type TaskFormErrors = Record<string, string>;

export type TaskFormResult = { ok: true; body: CreateTaskInput } | { ok: false; errors: TaskFormErrors };

/** The time of day a publish with no time set goes out at. */
export const DEFAULT_PUBLISH_TIME = '19:30';

/** `2026-10-23` + `19:30` in the browser's timezone, as the instant the API stores. Blank date = not scheduled. */
export function toScheduledAt(date: string, time: string): string | undefined {
  if (!date) return undefined;
  const at = new Date(`${date}T${time || DEFAULT_PUBLISH_TIME}`);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/** Blank optional fields are left out of the request rather than sent as '' (the API treats a sent value as meant). */
const blank = <T extends string>(value: T) => (value.trim() ? value : undefined);

function briefFrom(values: TaskFormValues): CreateTaskInput['brief'] {
  switch (values.type) {
    case 'post': {
      const p = values.post;
      return {
        type: 'post',
        format: p.format,
        platforms: p.platforms,
        caption: blank(p.caption),
        hashtags: p.hashtags,
        audioNote: blank(p.audioNote),
        scheduledUploadAt: toScheduledAt(p.publishDate, p.publishTime),
      };
    }
    case 'reel': {
      const r = values.reel;
      return {
        type: 'reel',
        script: r.script,
        durationSeconds: r.durationSeconds,
        platforms: r.platforms,
        caption: blank(r.caption),
        hashtags: r.hashtags,
        audioNote: blank(r.audioNote),
        scheduledUploadAt: toScheduledAt(r.publishDate, r.publishTime),
      };
    }
    case 'website': {
      const w = values.website;
      return {
        type: 'website',
        goalAudience: w.goalAudience,
        domainName: blank(w.domainName),
        domainRegistrar: blank(w.domainRegistrar),
        style: blank(w.style),
        primaryColor: blank(w.primaryColor),
        secondaryColor: blank(w.secondaryColor),
        fonts: blank(w.fonts),
        pages: w.pages.filter((p) => p.title.trim() || p.sections.length > 0),
        logoNotes: blank(w.logoNotes),
        generalNotes: blank(w.generalNotes),
        referenceSites: w.referenceSites,
        liveBy: blank(w.liveBy),
      };
    }
    case 'monthly_update': {
      const m = values.monthly;
      return {
        type: 'monthly_update',
        pageUrl: blank(m.pageUrl),
        changeKind: m.changeKind,
        description: m.description,
        liveBy: blank(m.liveBy),
      };
    }
    case 'custom':
      return { type: 'custom', details: values.custom.details };
  }
}

/** The task body without its project: what an update sends. */
export function taskBodyFrom(values: TaskFormValues, assets: BriefAsset[], draft: boolean) {
  return {
    title: values.title,
    draft,
    priority: values.priority,
    workUnits: values.workUnits,
    dueDate: blank(values.dueDate),
    clientVisible: values.clientVisible,
    requiresClientApproval: values.requiresClientApproval,
    milestones: values.milestones,
    assets,
    brief: briefFrom(values),
  };
}

/** Validates with the schema the API uses, so the page can never disagree with the server about what is valid. */
export function toTaskRequest(
  values: TaskFormValues,
  projectId: string,
  assets: BriefAsset[],
  draft: boolean,
): TaskFormResult {
  const body: CreateTaskInput = {
    projectId,
    ...taskBodyFrom(values, assets, draft),
    assigneeIds: values.team.map((m) => m.id),
    confirmOverCapacity: values.confirmOverCapacity,
  };
  const parsed = createTaskInputSchema.safeParse(body);
  if (parsed.success) return { ok: true, body };

  const errors: TaskFormErrors = {};
  for (const issue of parsed.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) errors[key] = issue.message;
  }
  return { ok: false, errors };
}

const dateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
/** Splits a stored instant back into the date and time inputs, in the browser's timezone. */
function splitScheduled(iso: string | null) {
  if (!iso) return { date: '', time: '' };
  const at = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`,
    time: `${pad(at.getHours())}:${pad(at.getMinutes())}`,
  };
}

/** The form for an existing draft, so it can be edited and submitted. */
export function formFromTask(task: TaskDetail): TaskFormValues {
  const form = emptyTaskForm(task.taskType);
  form.title = task.title;
  form.priority = task.priority;
  form.workUnits = task.workUnits;
  form.dueDate = dateInput(task.deadline);
  form.clientVisible = task.clientVisible;
  form.requiresClientApproval = task.requiresClientApproval;
  form.milestones = toMilestoneInputs(task.milestones);
  const details: BriefDetails | undefined = task.brief?.details;
  if (!details) return form;

  switch (details.type) {
    case 'post': {
      const when = splitScheduled(details.scheduledUploadAt);
      form.post = {
        format: details.format === 'carousel' ? 'carousel' : 'single_image',
        platforms: details.platforms.filter((p): p is Platform => p === 'instagram' || p === 'facebook'),
        caption: details.caption ?? '',
        hashtags: details.hashtags,
        publishDate: when.date,
        publishTime: when.time,
        audioNote: details.audioNote ?? '',
      };
      break;
    }
    case 'reel': {
      const when = splitScheduled(details.scheduledUploadAt);
      const seconds = details.durationSeconds;
      form.reel = {
        script: details.script ?? '',
        durationSeconds: seconds === 15 || seconds === 60 ? seconds : 30,
        platforms: details.platforms.filter((p): p is Platform => p === 'instagram' || p === 'facebook'),
        caption: details.caption ?? '',
        hashtags: details.hashtags,
        publishDate: when.date,
        publishTime: when.time,
        audioNote: details.audioNote ?? '',
      };
      break;
    }
    case 'website':
      form.website = {
        goalAudience: details.goalAudience ?? '',
        domainName: details.domainName ?? '',
        domainRegistrar: details.domainRegistrar ?? '',
        style: details.style ?? '',
        primaryColor: details.primaryColor ?? '',
        secondaryColor: details.secondaryColor ?? '',
        fonts: details.fonts ?? '',
        pages: details.pages,
        logoNotes: details.logoNotes ?? '',
        generalNotes: details.generalNotes ?? '',
        referenceSites: details.referenceSites,
        liveBy: details.liveBy ?? '',
      };
      break;
    case 'monthly_update': {
      const kinds = ['text_prices', 'images', 'layout', 'new_section', 'fix', 'other'] as const;
      form.monthly = {
        pageUrl: details.pageUrl ?? '',
        changeKind: kinds.find((k) => k === details.changeKind) ?? 'text_prices',
        description: details.description ?? '',
        liveBy: details.liveBy ?? '',
      };
      break;
    }
    case 'custom':
      form.custom = { details: details.details ?? '' };
      break;
  }
  return form;
}
